const moment = require('moment')
import Cookie from 'js-cookie'

class Util {
  /**
   * Apply any changes/normalization needed to a cube object so we can safely
   * and consistently use it in the app.
   *
   * @param cube
   */
  preprocessCube(cube) {
    // The backend can return either '1' or true as positive values.
    cube.hasProduction =
      cube.params.hasProduction === true || cube.params.hasProduction === '1'
    if (cube.hasProduction) {
      cube.productionType = cube.params.productionType.toLowerCase()
    }

    let connection = cube.connectionMetadata
    connection.isProducer = connection.isProducer === 1
    connection.isConsumer = connection.isConsumer === 1
    connection.donationConnection = connection.donationConnection === 1

    cube.isContributor =
      connection.donationConnection &&
      connection.isProducer &&
      connection.donator === cube.params.cubeName
    cube.isDonatee =
      connection.donationConnection &&
      connection.isConsumer &&
      connection.donatee === cube.params.cubeName
    cube.isProducer =
      cube.connectionStatus === 'producer' ||
      cube.connectionStatus === 'bidirectional'
    cube.isConsumer =
      cube.connectionStatus === 'consumer' ||
      cube.connectionStatus === 'bidirectional'
    cube.canReceiveDonations = cube.params.isDonationRecipient === '1'
    return cube
  }

  formatDate(date = false) {
    if (!date) {
      date = new Date()
    }

    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`
  }

  formatDateMonthYear(date = false) {
    if (!date) {
      date = new Date()
    }

    return `${date.getMonth() + 1}/${date.getFullYear()}`
  }

  formatDataPointDate(date) {
    let hours = date.getHours()
    if (hours < 10) {
      hours = '0' + hours
    }

    let minutes = date.getMinutes()
    if (minutes < 10) {
      minutes = '0' + minutes
    }

    return `${hours}:${minutes}`
  }

  formatPriceDate(date) {
    return moment(date).format('DD.MM.YYYY')
  }

  // Expects time slots in the format the device manager api returns:
  // 11:30-13:45,16:00-19:25,23:00-23:59
  // and returns an array of time slot objects.
  parseTimeSlots(raw) {
    if (raw.length < 1) {
      return []
    }
    return raw.split(',').map((slot) => this.parseTimeSlot(slot))
  }

  parseTimeSlot(raw) {
    const parts = raw.split('-')
    return {
      start: parts[0],
      end: parts[1],
    }
  }

  stringifyTimeSlot(slot) {
    return `${slot.start}-${slot.end}`
  }

  stringifyTimeSlots(slots) {
    return slots.map((slot) => this.stringifyTimeSlot(slot)).join(',')
  }

  canAddDeviceTimeSlot(lastSlot) {
    if (!lastSlot) {
      return true
    }
    const lastPossibleSlotStart = moment().hour(23).minute(59).second(0)
    const endParts = lastSlot.end.split(':')
    return moment()
      .hour(endParts[0])
      .minute(endParts[1])
      .second(0)
      .isSameOrBefore(lastPossibleSlotStart)
  }

  getNextDeviceTimeSlot(lastSlot) {
    let lastEnd = moment().minute(0)
    if (lastSlot) {
      lastEnd = moment(lastSlot.end, 'HH:mm')
    }
    let newStart = moment(lastEnd)
    if (lastEnd.hour() < 23) {
      newStart.hour(lastEnd.hour() + 1).minute(0)
    } else {
      newStart.minute(lastEnd.minute() + 1)
    }

    let newEnd = moment(newStart)
    if (newStart.hour() < 23) {
      newEnd.hour(newStart.hour() + 1)
    } else {
      newEnd.hour(23).minute(59)
    }

    const result = {
      start: newStart.format('HH:mm'),
      end: newEnd.format('HH:mm'),
    }
    return result
  }

  // Adds 1 minute to a given time and returns the result.
  // Format: HH:MM
  nextMinute(oldTime) {
    const parts = oldTime.split(':')
    return moment()
      .hour(parts[0])
      .minute(parts[1])
      .second(0)
      .add(1, 'minute')
      .format('HH:mm')
  }

  // Subtracts 1 minute to a given time and returns the result.
  // Format: HH:MM
  prevMinute(oldTime) {
    const parts = oldTime.split(':')
    return moment()
      .hour(parts[0])
      .minute(parts[1])
      .second(0)
      .subtract(1, 'minute')
      .format('HH:mm')
  }

  validateTimeSlot(start, end, prevEnd = false, nextStart = false) {
    const startTime = this.getMomentByTime(start)
    const endTime = this.getMomentByTime(end)
    const prevEndTime = prevEnd
      ? this.getMomentByTime(prevEnd)
      : moment().hour(0).minute(0).second(0)
    const nextStartTime = nextStart
      ? this.getMomentByTime(nextStart)
      : moment().hour(23).minute(59).second(0)
    return (
      startTime.isBefore(endTime) &&
      prevEndTime.isSameOrBefore(startTime) &&
      nextStartTime.isSameOrAfter(endTime)
    )
  }

  validateTimeSlots(slots) {
    slots = this.cloneTimeSlots(slots).sort(this.compareTimeSlots)
    return slots.reduce((result, slot, index) => {
      const prevSlot = index > 0 ? slots[index - 1] : false
      const nextSlot = index < slots.length - 1 ? slots[index + 1] : false
      return this.validateTimeSlot(
        slot.start,
        slot.end,
        prevSlot ? prevSlot.end : false,
        nextSlot ? nextSlot.start : false
      )
    }, true)
  }

  compareTimeSlots(a, b) {
    // Basic sorting start time by converting the str to an int.
    // E.g.: "12:39" -> 1239 and comparing these values.
    const startA = parseInt(a.start.replace(/[^0-9]+/, ''))
    const startB = parseInt(b.start.replace(/[^0-9]+/, ''))
    return startA - startB
  }

  getMomentByTime(time) {
    const parts = time.split(':')
    return moment().hour(parts[0]).minute(parts[1]).second(0)
  }

  // Note that we need to create a copy of each object in the slots array.
  // Otherwise changing the time inputs would directly manipulate the slot
  // objects in the store leading to weird issues since we'd be working around
  // vuex's way of modifying the store.
  cloneTimeSlots(slots) {
    return JSON.parse(JSON.stringify(slots))
  }

  timeSlotsAreEqual(slotA, slotB) {
    return slotA.start === slotB.start && slotA.end === slotB.end
  }

  getDeviceIndex(state, deviceId) {
    const deviceIndex = state.cube.devices.findIndex((device) => {
      return device.id === parseInt(deviceId)
    })
    if (deviceIndex === -1) {
      throw new Error(`Could not find device with id ${deviceId}.`)
    }
    return deviceIndex
  }

  canDoPasswordReset() {
    const minTimeBetweenResets = 20
    const lastPasswordReset = Cookie.get('lastPasswordReset')
    if (!lastPasswordReset) {
      return true
    }
    const now = Math.floor(Date.now() / 1000)
    const secondsElapsed = now - lastPasswordReset
    return secondsElapsed > minTimeBetweenResets
  }
}

export default new Util()
